import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Sparkles, ChevronRight, ChevronLeft } from "lucide-react";
import VerifiedTierBadge from "./VerifiedTierBadge";
import { hideOnError } from "../lib/imgFallback";
import { displayName, displayInitial } from "../lib/displayName";

/**
 * Horizontal-scroll carousel of practitioners who joined in the last 7 days.
 * Rendered on /feed above the post composer to amplify the same fan-out email
 * inside the app and give brand-new practitioners early discovery traffic.
 *
 * Empty state: silently renders nothing (no awkward "no one joined" message).
 */
export default function FeaturedNewPractitioners({ days = 7, limit = 12 }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const scroller = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/providers/featured-new?days=${days}&limit=${limit}`);
        if (!cancelled) setProviders(data?.providers || []);
      } catch {
        if (!cancelled) setProviders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, limit]);

  const scrollBy = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  if (loading || providers.length === 0) return null;

  return (
    <section
      className="parchment p-5 sm:p-6 relative"
      data-testid="featured-new-practitioners"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-secondary" />
          <div className="label-mystic text-foreground/80">
            New to the Cauldron · last {days} days
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            className="p-1.5 rounded-full hover:bg-foreground/5 text-foreground/60 hover:text-foreground transition"
            aria-label="Scroll left"
            data-testid="featured-new-scroll-left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            className="p-1.5 rounded-full hover:bg-foreground/5 text-foreground/60 hover:text-foreground transition"
            aria-label="Scroll right"
            data-testid="featured-new-scroll-right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scroller}
        className="flex gap-3 overflow-x-auto pb-1 snap-x"
        style={{ scrollbarWidth: "thin" }}
        data-testid="featured-new-scroller"
      >
        {providers.map((p) => (
          <button
            key={p.userId}
            type="button"
            onClick={() => nav(`/profile/${p.userId}`)}
            className="group w-[160px] flex-shrink-0 snap-start text-left bg-background/40 hover:bg-background/70 border border-foreground/10 hover:border-secondary/60 rounded-md p-3 transition shadow-sm hover:shadow-md"
            data-testid={`featured-new-card-${p.userId}`}
          >
            <div className="aspect-square w-full rounded-sm overflow-hidden bg-foreground/5 mb-3 ring-1 ring-foreground/10">
              {p.avatarUrl ? (
                <img
                  src={p.avatarUrl}
                  alt={displayName(p)}
                  onError={hideOnError}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-serif text-3xl text-foreground/40">
                  {(p.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>
            <div className="font-serif text-base leading-tight line-clamp-2 mb-1 text-foreground">
              {displayName(p)}
            </div>
            {p.tradition && (
              <div className="text-[11px] uppercase tracking-wider text-foreground/55 line-clamp-1">
                {p.tradition}
              </div>
            )}
            <div className="mt-2 flex items-center gap-1.5">
              <VerifiedTierBadge tier={p.verifiedTier} size={12} compact />
              <span className="text-[10px] uppercase tracking-wider text-secondary/80 ml-auto opacity-0 group-hover:opacity-100 transition">
                View →
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
